/* eslint-disable @faire/no-dollar-symbol-in-strings */

/* eslint-disable no-console */
import { Builder, builder } from "@builder.io/sdk";

import { isDebug } from "./is-debug";
import { safeDynamicRequire } from "./safe-dynamic-require";

export const api = (_: any) => builder;

export const makeFn = (code: string, useReturn: boolean, args?: string[]) => {
  // Order must match the order of the arguments to the function
  const names = args || [
    "state",
    "event",
    "block",
    "builder",
    "Device",
    "update",
    "Builder",
    "context",
  ];

  // Convert all argument references to proxies, and pass `copySync` method to target object, to return a copy of the original JS object
  // https://github.com/laverdet/isolated-vm#referencecopysync
  const refToProxyFn = `
  var refToProxy = (obj) => {
    if (typeof obj !== 'object' || obj === null) {
      return obj;
    }
    return new Proxy({}, {
        get(target, key) {
            if (key === 'copySync') {
              return () => obj.copySync();
            }
            const val = obj.getSync(key);
            if (typeof val?.copySync === 'function') {
                return JSON.parse(stringify(val));
            }
            return val;
        },
        set(target, key, value) {
            obj.setSync(key, value);
        },
        deleteProperty(target, key) {
            obj.deleteSync(key);
        }
      })
  }
`;
  // Returned object  will be stringified and parsed back to the parent isolate
  const strinfigyFn = `
    var stringify = (val) => {
      if (typeof val === 'object' && val !== null) {
        return JSON.stringify(val.copySync ? val.copySync() : val);
      }
      return val;
    }
  `;

  return `
${refToProxyFn}
${strinfigyFn}
`.concat(
    names.map((arg, index) => `var ${arg} = refToProxy($${index});`).join("\n")
  ).concat(`
${names.includes("context") ? "var ctx = context;" : ""}
var endResult = function() {
  ${useReturn ? `return (${code});` : code};
};

return stringify(endResult());
`);
};

export const getIsolateContext = () => {
  let isolatedContext = Builder.serverContext;

  if (!isolatedContext) {
    const ivm = safeDynamicRequire(
      "isolated-vm"
    ) as typeof import("isolated-vm");
    const isolate = new ivm.Isolate({ memoryLimit: 128 });
    isolatedContext = isolate.createContextSync();
    Builder.setServerContext(isolatedContext);
  }
  const jail = isolatedContext!.global;
  // This makes the global object available in the context as `global`. We use `derefInto()` here
  // because otherwise `global` would actually be a Reference{} object in the new isolate.
  jail.setSync("global", jail.derefInto());
  // We will create a basic `log` function for the new isolate to use.
  jail.setSync("log", function (...args: any[]) {
    if (isDebug()) {
      console.log(...args);
    }
  });
  return isolatedContext;
};
